import { OrganizerProfileCard, Skeleton } from '@h2hspark/ui';
import { Check } from 'lucide-react';
import * as React from 'react';

import type { PublicOrganizerProfile } from './api';
import { PROFILE_COPY, organizerLinks } from './constants';
import { FollowOrganizerCard } from './follow-organizer-card';
import { OrganizerEventsPanel } from './organizer-events-panel';

/**
 * The organizer public profile, below the chrome (`MEM-018`).
 *
 * ## One panel, two routes — the answer to "one profile, two contexts"
 *
 * The same profile is reachable at `h2hspark.com/{slug}` by a signed-out visitor in marketing
 * chrome, and at `/app/organizers/{slug}` by a member who tapped the host on an event, inside the
 * app shell. **Everything below the chrome is this file**, rendered by both routes over the same
 * queries — see `constants.ts`'s `memberProfilePath` for why the split is at the route rather
 * than inside a component that sniffs its own context.
 *
 * The only thing the two routes pass differently is `backSlot`: the app screen puts a back link
 * above the header, and the marketing page does not, because it has a site header and a visitor
 * arriving there did not come from anywhere in particular.
 *
 * ## Transcribed from `Web App.dc.html` (10 September), `showOrgProfile`, §2.8
 *
 * The **header** — an 88px tile, the name, the `map-pin {basedIn} · calendar Since {year}` meta
 * row, the introduction, and **Follow organizer** opposite — is `OrganizerProfileCard`'s `profile`
 * variant, so `ORG-017` AC-4 still holds; then **Upcoming events** as a three-column card grid and
 * **Past events** as a list, both `OrganizerEventsPanel`'s.
 *
 * **It used to be a full-bleed gradient band over a single narrow column**, from the phone
 * prototype, and on the marketing route that column was 390px wide inside a 1200px site. Sumit's
 * ruling of 2026-09-11 is one app with a responsive layout, and `Web App.dc.html` is the design at
 * every width; the width itself is the route's (`public-profile-page.tsx` for the site, the shell's
 * canvas inside `/app`), because a panel that sets its own is a panel that fights whichever chrome
 * it lands in.
 *
 * ## What the prototype draws here and this does not: the statistics row
 *
 * §2.8 draws four tiles — *18 Events hosted · 412 Members hosted · 4.9 Average rating · 68% Return
 * rate*. **Sumit ruled the whole row out on 2026-09-11:** *"let's remove all stat tiles (whole
 * row) for now"*. That replaced his earlier answer that same day (four tiles, true figures only),
 * and it leaves `MEM-018` AC-7 standing exactly as written — nothing here exposes member-level
 * data, and now nothing here is a statistic at all. `docs/plans/rulings-2026-09-11.md` answer 6,
 * `handoff-corrections.md` #57.
 *
 * Worth keeping straight, because it is the question an organizer asks: three of the four could
 * not be served honestly in any case. There is no rating anywhere in the platform, and *Members
 * hosted* and *Return rate* are aggregates over the people who attended, on a page a signed-out
 * visitor loads, with no five-member floor beneath them. The fourth, *Events hosted*, is the
 * length of the Past events list immediately below.
 *
 * ## What is here that the prototype does not draw
 *
 * The gallery (`ORG-018`), the interest tags and the accessibility commitments, all of which
 * `ORG-016` and `ORG-018` collect and the public profile rendered before this story. They stay:
 * removing content an organizer has filled in, because a wireframe drawn later does not show it,
 * would be a regression wearing parity's clothes.
 */
export function OrganizerProfilePanel({
  profile,
  backSlot,
}: {
  readonly profile: PublicOrganizerProfile;
  /** The app shell's back link, above the header. Absent on the marketing route. */
  readonly backSlot?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="pb-10">
      {backSlot}

      <OrganizerProfileCard
        variant="profile"
        organizerName={profile.organizerName}
        metaLine={profile.metaLine}
        introduction={profile.introduction}
        basedIn={profile.basedIn}
        hostingSinceYear={profile.hostingSinceYear}
        // `MEM-060` AC-3: `null` while the organizer has set no Host, and the header draws no line.
        hostLabel={profile.hostLabel}
        // The API mints this address on the read that produced `profile`, because the bucket is
        // private and the address expires. `null` is the real state for an organizer who has
        // uploaded no logo, and the card draws the initials tile (`ORG-015` AC-2).
        logoUrl={profile.logoUrl}
        // §2.8 puts Follow organizer at the right of the header. The control itself is the app's,
        // because it needs a session, a mutation and an error path.
        actionSlot={<FollowOrganizerCard slug={profile.slug} />}
        // The organizer's own links, as icons on the meta line (Sumit, 2026-09-12). Composed here
        // rather than in the component because only this app knows the payload's shape, and
        // composed from the payload's own two fields so a channel the organization has not filled
        // in is simply absent rather than drawn as a dead icon.
        links={organizerLinks(profile)}
      />

      <div className="space-y-9">
        {profile.interestTags.length === 0 ? null : (
          <section>
            <h2 className="mb-3.5 text-xl md:text-2xl">What they gather around</h2>
            <ul className="flex flex-wrap gap-[9px]">
              {profile.interestTags.map((tag) => (
                <li
                  key={tag.termId}
                  className="inline-flex h-9 items-center rounded-pill bg-accent px-[15px] font-brand text-sm font-semibold text-accent-foreground"
                >
                  {tag.displayName}
                </li>
              ))}
            </ul>
          </section>
        )}

        {profile.accessibilityCommitments.length === 0 ? null : (
          <section>
            <h2 className="mb-3.5 text-xl md:text-2xl">What they always promise</h2>
            <ul className="flex flex-wrap gap-[9px]">
              {profile.accessibilityCommitments.map((commitment) => (
                <li
                  key={commitment.termId}
                  className="inline-flex h-9 items-center gap-[7px] rounded-pill bg-success/15 px-[15px] font-brand text-sm font-semibold text-success"
                >
                  <Check className="h-3.5 w-3.5" aria-hidden />
                  {commitment.displayName}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* The events come after what the organizer gathers people around and what they always
            promise, in Brad's design of 27 October (TW 44540823, Sumit 2026-10-28, `MEM-060`
            AC-5). They led the page from 10 September; the design reads it as "who is this, then
            what are they running", which is the order a first-time reader asks in. */}
        <OrganizerEventsPanel slug={profile.slug} />

        {/*
          **At the foot of the page, beneath the events** — Sumit, 2026-09-12: *"lets move photos
          from event to below past events"*. `ORG-018` AC-1 said *"beneath the introduction"* and
          is reworded to match; ruling 55.

          It sat immediately under the About section on the argument that it answers the question
          the introduction raises. What that missed is what a member came for: an organizer's page
          is read to decide whether to go to something, and twelve photographs between the
          introduction and the first event pushed every event below the fold. The photographs are
          the thing you look at once you are interested, which is the bottom.

          Nothing renders for an organizer with no photographs. An empty frame on a public page
          says the organizer failed to do something, which is not true — the gallery is optional
          and most profiles will not have one.
        */}
        {profile.gallery.length === 0 ? null : (
          <section>
            <div className="mb-3.5 flex items-baseline justify-between gap-4">
              <h2 className="text-xl md:text-2xl">{PROFILE_COPY.galleryHeading}</h2>
              <span className="text-sm text-muted-foreground">
                {PROFILE_COPY.galleryCount(profile.gallery.length)}
              </span>
            </div>
            {/* A mosaic (`MEM-060` AC-8): the first photo over two rows and two columns, the rest
                square around it. `grid-flow-dense` lets the small ones fill the gap the large one
                leaves, so the grid has no holes at any count. */}
            <ul className="grid grid-flow-dense grid-cols-2 gap-3 sm:grid-cols-4">
              {profile.gallery.map((image, position) => (
                <li
                  key={image.objectId}
                  className={position === 0 && profile.gallery.length > 1 ? 'col-span-2 row-span-2' : undefined}
                >
                  <img
                    src={image.url}
                    alt={PROFILE_COPY.galleryImageAlt(
                      profile.organizerName,
                      position + 1,
                      profile.gallery.length,
                    )}
                    loading="lazy"
                    className="aspect-square h-full w-full rounded-lg object-cover"
                  />
                </li>
              ))}
            </ul>
          </section>
        )}

      </div>
    </div>
  );
}

/** The loading state, shaped like the header and the first grid rather than a grey slab. */
export function OrganizerProfileSkeleton(): React.ReactElement {
  return (
    <div className="pb-10" aria-busy>
      <div className="mb-9 rounded-xl border border-border bg-card p-5 md:p-7">
        <div className="flex items-start gap-4 md:gap-[18px]">
          <Skeleton className="h-[72px] w-[72px] flex-none rounded-xl" />
          <div className="min-w-0 flex-1 space-y-3">
            <Skeleton className="h-9 w-2/3" />
            <Skeleton className="h-4 w-1/3" />
          </div>
        </div>
        <Skeleton className="mt-5 h-14 w-full" />
      </div>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Skeleton className="h-[210px] w-full rounded-xl" />
        <Skeleton className="h-[210px] w-full rounded-xl" />
        <Skeleton className="h-[210px] w-full rounded-xl" />
      </div>
    </div>
  );
}

/**
 * What answers an address no organizer claims.
 *
 * The wording is the one this page has always used, and it is the specific claim rather than a
 * general 404 — a reader who followed an organizer's shared link needs to know the *organizer* is
 * not there, which is different from "this page does not exist". `SlugRoutePage` still falls back
 * to the site's own 404 for an address nothing at all claims.
 */
export function NoOrganizerHere(): React.ReactElement {
  return (
    <div className="mx-auto w-full max-w-2xl px-6 py-24 text-center">
      <h1 className="mb-3 text-2xl tracking-tight text-foreground">
        There is no organizer at this address
      </h1>
      <p className="text-md text-muted-foreground">
        The link may be out of date — an organizer can change their profile address, and the old
        one stops working when they do.
      </p>
    </div>
  );
}
